import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [deviceLimit, setDeviceLimit] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [recStep, setRecStep] = useState("request");
  const [recCode, setRecCode] = useState("");
  const [recSending, setRecSending] = useState(false);
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();

  const goNext = () => {
    const returnTo = new URLSearchParams(window.location.search).get("returnTo");
    navigate(returnTo && returnTo.startsWith("/") ? returnTo : "/select-module");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfaRequired) {
        setMfaToken(res.mfaToken);
        toast.info("Enter the 6-digit code from your authenticator app");
        return;
      }
      if (res?.mustChangePassword) {
        navigate("/set-password");
        return;
      }
      toast.success("Welcome back");
      if (res?.mfaSetupRequired) {
        toast.warning("Your organisation requires two-factor authentication — set it up in Settings");
      }
      goNext();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 403 && d && typeof d === "object" && d.code === "DEVICE_LIMIT_REACHED") {
        setDeviceLimit(d.max_devices || 2);
        return;
      }
      toast.error((typeof d === "string" ? d : null) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onVerifyMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyMfa(mfaToken, mfaCode);
      toast.success("Welcome back");
      goNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  if (mfaToken) {
    return <AuthShell title="Two-factor verification" subtitle="Enter your authenticator code">
      <form onSubmit={onVerifyMfa} className="space-y-5" data-testid="mfa-login-form">
        <Field label="6-digit code (or a backup code)" type="text" value={mfaCode}
          onChange={setMfaCode} testid="mfa-code-input" required />
        <button data-testid="mfa-verify-button" type="submit" disabled={loading}
          className="w-full h-14 px-8 inline-flex items-center justify-center gap-2 font-bold text-lg bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 transition-colors">
          {loading ? <><Loader2 className="animate-spin" size={18}/>Verifying…</> : "Verify"}
        </button>
        <button type="button" onClick={() => { setMfaToken(""); setMfaCode(""); }}
          className="w-full text-center text-sm text-slate-500 underline">Back to sign in</button>
      </form>
    </AuthShell>;
  }

  const startRecovery = () => {
    setDeviceLimit(null);
    setRecovering(true);
    setRecStep("request");
    setRecCode("");
  };

  const onRequestRecovery = async (e) => {
    e.preventDefault();
    setRecSending(true);
    try {
      await api.post("/auth/device-recovery/request", { email });
      setRecStep("verify");
      toast.success("If an account exists for that email, a 6-digit code is on its way.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the code. Try again.");
    } finally {
      setRecSending(false);
    }
  };

  const onVerifyRecovery = async (e) => {
    e.preventDefault();
    setRecSending(true);
    try {
      await api.post("/auth/device-recovery/verify", { email, code: recCode, password });
      toast.success("Devices reset — signing you in…");
      const res = await login(email, password);
      setRecovering(false);
      if (res?.mfaRequired) {
        setMfaToken(res.mfaToken);
        toast.info("Enter the 6-digit code from your authenticator app");
        return;
      }
      goNext();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : null) || "Verification failed");
    } finally {
      setRecSending(false);
    }
  };

  if (recovering) {
    return <AuthShell title="Recover access" subtitle="Reset your devices">
      {recStep === "request" ? (
        <form onSubmit={onRequestRecovery} className="space-y-5" data-testid="recovery-request-form">
          <p className="text-[13px] text-slate-600 leading-relaxed">
            Locked out because your device limit is full? We'll email a 6-digit code. Enter it with your
            password to remove your old devices and sign in here. Your inspections, photos and reports are never touched.
          </p>
          <Field label="Email" type="email" value={email} onChange={setEmail} testid="recovery-email" required />
          <button data-testid="recovery-request-submit" type="submit" disabled={recSending}
            className="w-full h-14 px-8 inline-flex items-center justify-center gap-2 font-bold text-lg bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 transition-colors">
            {recSending ? <><Loader2 className="animate-spin" size={18}/>Sending…</> : "Send recovery code"}
          </button>
          <button type="button" onClick={() => setRecovering(false)}
            className="w-full text-center text-sm text-slate-500 underline" data-testid="recovery-cancel">Back to sign in</button>
        </form>
      ) : (
        <form onSubmit={onVerifyRecovery} className="space-y-5" data-testid="recovery-verify-form">
          <p className="text-[13px] text-slate-600 leading-relaxed">
            Enter the 6-digit code sent to <b>{email}</b> and your account password. This removes all
            registered devices so you can sign in on this one.
          </p>
          <Field label="6-digit code" type="text" value={recCode} onChange={setRecCode} testid="recovery-code" required />
          <Field label="Account password" type="password" value={password} onChange={setPassword} testid="recovery-password" required />
          <button data-testid="recovery-verify-submit" type="submit" disabled={recSending}
            className="w-full h-14 px-8 inline-flex items-center justify-center gap-2 font-bold text-lg bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 transition-colors">
            {recSending ? <><Loader2 className="animate-spin" size={18}/>Verifying…</> : "Verify & reset devices"}
          </button>
          <div className="flex items-center justify-between text-sm">
            <button type="button" onClick={onRequestRecovery} className="text-teal-700 underline font-bold" data-testid="recovery-resend">Resend code</button>
            <button type="button" onClick={() => setRecovering(false)} className="text-slate-500 underline" data-testid="recovery-verify-cancel">Back to sign in</button>
          </div>
        </form>
      )}
    </AuthShell>;
  }

  return <AuthShell title="Sign in" subtitle="Inspector portal">
    {deviceLimit && (
      <div className="mb-5 border-2 border-amber-300 bg-amber-50 p-4" data-testid="device-limit-modal">
        <p className="text-sm font-black text-amber-900">Device limit reached</p>
        <p className="text-[13px] text-amber-800 mt-1 leading-relaxed">
          Your Solo subscription allows Compliance Hub to be used on up to {deviceLimit} devices.
          Reset your devices to sign in here, or remove one from
          <span className="font-semibold"> Settings → Account → Devices</span> on a device you're already signed in on.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={startRecovery}
            className="h-9 px-3 inline-flex items-center bg-[#1E1B4B] text-white font-bold text-xs" data-testid="device-limit-recover">Reset my devices</button>
          <a href="mailto:pascertification@gmail.com?subject=Compliance%20Hub%20device%20reset"
            className="h-9 px-3 inline-flex items-center border-2 border-slate-300 text-slate-700 font-bold text-xs" data-testid="device-limit-manage">Contact support</a>
          <button onClick={() => setDeviceLimit(null)} className="h-9 px-3 border-2 border-slate-300 font-bold text-xs text-slate-700" data-testid="device-limit-cancel">Cancel</button>
        </div>
      </div>
    )}
    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
      <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" required />
      <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" required />
      <button
        data-testid="login-submit-button"
        type="submit"
        disabled={loading}
        className="w-full h-14 px-8 inline-flex items-center justify-center gap-2 font-bold text-lg bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 transition-colors"
      >
        {loading ? <><Loader2 className="animate-spin" size={18}/>Signing in…</> : "Sign in"}
      </button>
      <p className="text-center text-sm text-slate-600">
        New inspector? <Link to="/register" data-testid="link-to-register" className="font-bold text-[#1E1B4B] underline">Create an account</Link>
      </p>
      <p className="text-center text-sm">
        <Link to="/forgot-password" data-testid="link-to-forgot" className="font-bold text-teal-700 underline">Forgot your password?</Link>
      </p>
      <p className="text-center text-xs text-slate-500 mt-1">
        On your phone? <Link to="/install" data-testid="link-to-install" className="font-bold text-teal-700 underline">Install the app</Link> for offline access
      </p>
    </form>
  </AuthShell>;
};

export const Register = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [orgType, setOrgType] = useState("private_certifier");
  const { register } = useAuth();
  const navigate = useNavigate();

  // Look up the invite so we can pre-fill the email + show who invited you.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    api.get(`/team/invite/lookup/${inviteToken}`)
      .then((r) => {
        if (cancelled) return;
        setInvitePreview(r.data);
        setEmail(r.data.email || "");
        if (r.data.invited_name) setName(r.data.invited_name);
      })
      .catch((err) => {
        if (cancelled) return;
        setInviteError(err?.response?.data?.detail || "This invitation link is no longer valid.");
      });
    return () => { cancelled = true; };
  }, [inviteToken]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name, inviteToken || undefined, inviteToken ? undefined : orgType);
      toast.success(invitePreview ? `Joined ${invitePreview.owner_company || invitePreview.owner_name}` : "Account created");
      navigate("/select-module");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return <AuthShell title={invitePreview ? "Join your team" : "Create account"} subtitle={invitePreview ? "Team invitation" : "Inspector portal"}>
    {inviteToken && !invitePreview && !inviteError && (
      <div data-testid="invite-loading" className="mb-5 p-3 border-2 border-slate-200 bg-slate-50 text-sm text-slate-600 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin"/> Checking invitation…
      </div>
    )}
    {inviteError && (
      <div data-testid="invite-error" className="mb-5 p-4 border-2 border-red-200 bg-red-50 text-sm text-red-900">
        {inviteError}
      </div>
    )}
    {invitePreview && (
      <div data-testid="invite-banner" className="mb-5 p-4 border-2 border-teal-200 bg-teal-50">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-teal-900 flex items-center gap-1.5">
          <Users size={12} strokeWidth={3}/> You&apos;ve been invited
        </p>
        <p className="text-sm text-slate-900 mt-2 leading-relaxed">
          <strong>{invitePreview.owner_name || "Your team"}</strong>
          {invitePreview.owner_company ? <> from <strong>{invitePreview.owner_company}</strong></> : null}
          {" "}invited you to join their team.
          You&apos;ll get their <strong className="uppercase">{invitePreview.tier}</strong> plan features.
        </p>
      </div>
    )}
    <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
      <Field label="Full name" value={name} onChange={setName} testid="register-name" required />
      <Field label="Email" type="email" value={email} onChange={setEmail} testid="register-email" required readOnly={!!invitePreview} />
      <Field label="Password" type="password" value={password} onChange={setPassword} testid="register-password" required minLength={6} />
      {!invitePreview && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">I work as a…</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "private_certifier", label: "Private Certifier", hint: "I notify council after inspections" },
              { v: "council", label: "Council", hint: "I inspect on behalf of a council" },
            ].map((o) => (
              <button type="button" key={o.v} onClick={() => setOrgType(o.v)}
                data-testid={`register-org-${o.v}`}
                className={`text-left border-2 p-3 transition-colors ${orgType === o.v ? "border-[#1E1B4B] bg-[#1E1B4B]/5" : "border-slate-200 hover:border-slate-400"}`}>
                <span className="block font-black text-sm text-slate-900">{o.label}</span>
                <span className="block text-[11px] text-slate-500 mt-0.5">{o.hint}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <button
        data-testid="register-submit-button"
        type="submit"
        disabled={loading || !!inviteError}
        className="w-full h-14 px-8 inline-flex items-center justify-center gap-2 font-bold text-lg bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 transition-colors"
      >
        {loading ? <><Loader2 className="animate-spin" size={18}/>Creating…</> : (invitePreview ? "Join team" : "Create account")}
      </button>
      <p className="text-center text-sm text-slate-600">
        Already have an account? <Link to={inviteToken ? `/login?invite=${inviteToken}` : "/login"} data-testid="link-to-login" className="font-bold text-[#1E1B4B] underline">Sign in</Link>
      </p>
    </form>
  </AuthShell>;
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch { setSent(true); }
    finally { setLoading(false); }
  };
  return <AuthShell title="Reset password" subtitle="We'll email you a link">
    {sent ? (
      <div data-testid="forgot-sent" className="space-y-4">
        <p className="text-slate-700">If an account exists for <b>{email}</b>, a password-reset link is on its way. It's valid for one hour.</p>
        <Link to="/login" className="font-bold text-[#1E1B4B] underline">Back to sign in</Link>
      </div>
    ) : (
      <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="forgot-email" required />
        <button data-testid="forgot-submit" type="submit" disabled={loading}
          className="w-full h-14 px-8 inline-flex items-center justify-center gap-2 font-bold text-lg bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 transition-colors">
          {loading ? <><Loader2 className="animate-spin" size={18}/>Sending…</> : "Send reset link"}
        </button>
        <Link to="/login" className="block text-center text-sm text-slate-500 underline">Back to sign in</Link>
      </form>
    )}
  </AuthShell>;
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset — please sign in");
      navigate("/login");
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
    finally { setLoading(false); }
  };
  return <AuthShell title="Choose a new password" subtitle="Minimum 10 characters, mixed types">
    <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
      <Field label="New password" type="password" value={password} onChange={setPassword} testid="reset-password" required minLength={10} />
      <button data-testid="reset-submit" type="submit" disabled={loading || !token}
        className="w-full h-14 px-8 inline-flex items-center justify-center gap-2 font-bold text-lg bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 transition-colors">
        {loading ? <><Loader2 className="animate-spin" size={18}/>Saving…</> : "Set new password"}
      </button>
      {!token && <p className="text-center text-sm text-red-600">This reset link is missing its token.</p>}
    </form>
  </AuthShell>;
};


const Field = ({ label, value, onChange, testid, type = "text", required, minLength, readOnly }) => (
  <label className="block">
    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      minLength={minLength}
      readOnly={readOnly}
      className={`h-14 w-full text-lg px-4 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none rounded-none transition-colors ${readOnly ? "bg-slate-50 cursor-not-allowed" : ""}`}
    />
  </label>
);

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-white">
    <div className="hidden lg:flex relative overflow-hidden bg-black items-center justify-center">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(20,184,166,0.18),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(49,46,129,0.35),transparent_60%)]" />
      <div className="relative flex flex-col items-center justify-between h-full py-12 px-12 text-white">
        <div className="self-start text-xs uppercase tracking-[0.3em] font-bold text-teal-300/70">Inspector Portal</div>
        <div className="flex flex-col items-center gap-6">
          <img
            src="/ch-logo-420.png"
            srcSet="/ch-logo-420.png 1x, /ch-logo-840.png 2x"
            alt="Compliance Hub"
            width="300" height="300"
            className="w-[300px] max-w-[70%] h-auto object-contain drop-shadow-[0_10px_40px_rgba(0,242,195,0.25)]"
            loading="eager" decoding="async"
          />
          <div className="text-center">
            <p className="text-4xl font-black tracking-[0.08em] text-white">COMPLIANCE</p>
            <p className="text-2xl font-bold tracking-[0.45em] text-[#00F2C3] mt-1">HUB</p>
            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mt-4">All inspections. One hub.</p>
          </div>
        </div>
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-teal-300/70 self-start">© Compliance Hub</p>
      </div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-md">
        <div className="lg:hidden mb-8 flex justify-center">
          <img
            src="/ch-logo-256.png"
            srcSet="/ch-logo-256.png 1x, /ch-logo-420.png 2x"
            alt="Compliance Hub"
            width="180" height="180"
            className="w-44 h-auto object-contain"
            loading="eager" decoding="async"
          />
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{subtitle}</p>
        <h1 className="text-4xl font-black tracking-tight text-slate-900 mt-1 mb-8">{title}</h1>
        {children}
      </div>
    </div>
  </div>
);
